/**
 * <quick-atc-drawer> web component
 *
 * Opens a slide-in drawer with an image gallery (Embla Carousel),
 * product info, variant picker, and ATC button.
 * Desktop: slides from the right.  Mobile (<768px): slides from the bottom.
 */
if (!customElements.get('quick-atc-drawer')) {
  customElements.define(
    'quick-atc-drawer',
    class QuickAtcDrawer extends HTMLElement {
      constructor() {
        super();
        this.clone = null;
        this.embla = null;
        this._onKeyDown = this._onKeyDown.bind(this);
      }

      /** Open the drawer: clone template -> body, animate in, bind events */
      open() {
        const tpl = this.querySelector('template');
        if (!tpl) return;

        this.clone = tpl.content.cloneNode(true);
        this.wrapper = document.createElement('div');
        this.wrapper.classList.add('quick-atc-drawer__wrapper');
        this.wrapper.appendChild(this.clone);
        document.body.appendChild(this.wrapper);

        // Cache references
        this.panel = this.wrapper.querySelector('.quick-atc-drawer__panel');
        this.overlay = this.wrapper.querySelector('[data-overlay]');
        this.priceEl = this.wrapper.querySelector('[data-price]');
        this.unitPriceEl = this.wrapper.querySelector('[data-unit-price]');
        this.variantInput = this.wrapper.querySelector('[data-variant-id]');
        this.atcBtn = this.wrapper.querySelector('[data-atc-btn]');
        this.selects = [
          ...this.wrapper.querySelectorAll('.quick-atc-drawer__select'),
        ];

        // Parse embedded variants JSON
        const variantsScript = this.wrapper.querySelector('[data-variants]');
        this.variants = variantsScript
          ? JSON.parse(variantsScript.textContent)
          : [];

        // Parse media map (variantId -> mediaId)
        const mediaMapScript = this.wrapper.querySelector('[data-media-map]');
        this.mediaMap = mediaMapScript
          ? JSON.parse(mediaMapScript.textContent)
          : {};

        // Parse price breaks per variant (for volume pricing / Mengenkauf)
        const priceBreaksScript =
          this.wrapper.querySelector('[data-price-breaks]');
        this.priceBreaks = priceBreaksScript
          ? JSON.parse(priceBreaksScript.textContent)
          : null;

        // Quantity input
        this.qtyInput = this.wrapper.querySelector('[data-qty-input]');
        this.volumePricingEl =
          this.wrapper.querySelector('[data-volume-pricing]');

        // Prevent background scroll
        document.body.classList.add('quick-atc-drawer-open');

        // Animate in
        requestAnimationFrame(() => {
          this.wrapper.classList.add('is-active');
          requestAnimationFrame(() => {
            this.wrapper.classList.add('is-visible');
          });
        });

        // Close triggers
        this.overlay.addEventListener('click', () => this.close());
        this.wrapper
          .querySelector('[data-close]')
          .addEventListener('click', () => this.close());
        document.addEventListener('keydown', this._onKeyDown);

        // Variant change
        this.selects.forEach((sel) =>
          sel.addEventListener('change', () => this._onOptionChange())
        );

        // Quantity +/- buttons
        const minusBtn = this.wrapper.querySelector('[data-qty-minus]');
        const plusBtn = this.wrapper.querySelector('[data-qty-plus]');
        if (minusBtn) {
          minusBtn.addEventListener('click', () => this._onQtyBtn('minus'));
        }
        if (plusBtn) {
          plusBtn.addEventListener('click', () => this._onQtyBtn('plus'));
        }
        if (this.qtyInput) {
          this.qtyInput.addEventListener('change', () => {
            this._onQtyChange();
          });
        }

        // ATC auto-close
        this._productForm = this.wrapper.querySelector('product-form');
        if (this._productForm) {
          /* eslint-disable no-undef */
          this._cartUpdateUnsub = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            (evt) => {
              if (
                evt?.productVariantId ===
                String(this.variantInput?.value)
              ) {
                this.close();
              }
            }
          );
          /* eslint-enable no-undef */
        }

        // Init Embla Carousel
        this._initGallery();

        // Focus
        this._trapFocus();
      }

      /** Animate out, clean up */
      close() {
        if (!this.wrapper) return;

        this.wrapper.classList.remove('is-visible');
        const onEnd = () => {
          if (this.embla) {
            this.embla.destroy();
            this.embla = null;
          }
          this.wrapper.remove();
          this.wrapper = null;
          document.body.classList.remove('quick-atc-drawer-open');
          document.removeEventListener('keydown', this._onKeyDown);
          if (this._cartUpdateUnsub) {
            this._cartUpdateUnsub();
            this._cartUpdateUnsub = null;
          }
        };

        this.panel.addEventListener('transitionend', onEnd, { once: true });
        setTimeout(onEnd, 400);
      }

      // ---- Gallery ---------------------------------------------------------

      _initGallery() {
        const viewport = this.wrapper.querySelector(
          '[data-gallery-viewport]'
        );
        // eslint-disable-next-line no-undef
        if (!viewport || typeof EmblaCarousel === 'undefined') return;

        // eslint-disable-next-line no-undef
        this.embla = EmblaCarousel(viewport, {
          loop: false,
          align: 'start',
          containScroll: 'trimSnaps',
          dragFree: false,
        });

      }

      _scrollToVariantImage(variantId) {
        if (!this.embla) return;
        const mediaId = this.mediaMap[String(variantId)];
        if (!mediaId) return;

        const slides = this.wrapper.querySelectorAll(
          '.quick-atc-drawer__gallery-slide'
        );
        slides.forEach((slide, i) => {
          if (String(slide.dataset.variantImgId) === String(mediaId)) {
            this.embla.scrollTo(i);
          }
        });
      }

      // ---- Variant change --------------------------------------------------

      _onKeyDown(e) {
        if (e.key === 'Escape') this.close();
      }

      _onOptionChange() {
        const selectedOptions = this.selects.map((sel) => sel.value);

        const variant = this.variants.find((v) =>
          v.options.every((opt, i) => opt === selectedOptions[i])
        );

        if (!variant) {
          this.atcBtn.setAttribute('disabled', 'disabled');
          return;
        }

        this.variantInput.value = variant.id;
        this._updatePrice(variant);
        this._scrollToVariantImage(variant.id);
        this._updateQtyRules(variant);
        this._updateVolumePricing(variant);

        if (variant.available) {
          this.atcBtn.removeAttribute('disabled');
        } else {
          this.atcBtn.setAttribute('disabled', 'disabled');
        }
      }

      _updatePrice(variant) {
        if (!this.priceEl) return;

        const isSale =
          variant.compare_at_price &&
          variant.compare_at_price > variant.price;
        let html = '<div class="quick-atc-drawer__price-row">';
        if (isSale) {
          html +=
            '<span class="quick-atc-drawer__compare-price">' +
            `${this._formatMoney(variant.compare_at_price)}</span>`;
        }
        html +=
          `<span class="quick-atc-drawer__current-price"${
            isSale ? ' style="color:#DB1420;"' : ''
          }>` +
          `${this._formatMoney(variant.price)}</span></div>`;

        // Unit price
        if (variant.unit_price_measurement) {
          const ref = variant.unit_price_measurement;
          html +=
            '<span class="quick-atc-drawer__unit-price">' +
            `${this._formatMoney(variant.unit_price)}` +
            `/${ref.reference_value}${ref.reference_unit}` +
            '</span>';
        }

        this.priceEl.innerHTML = html;
      }

      // eslint-disable-next-line class-methods-use-this
      _formatMoney(cents) {
        // eslint-disable-next-line no-undef
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
          // eslint-disable-next-line no-undef
          return Shopify.formatMoney(cents, Shopify.money_format);
        }
        const amount = (cents / 100).toFixed(2).replace('.', ',');
        return `${amount} \u20AC`;
      }

      // ---- Quantity --------------------------------------------------------

      _onQtyBtn(dir) {
        if (!this.qtyInput) return;
        const prev = this.qtyInput.value;
        if (dir === 'plus') {
          this.qtyInput.stepUp();
        } else {
          this.qtyInput.stepDown();
        }
        if (prev !== this.qtyInput.value) this._onQtyChange();
      }

      _onQtyChange() {
        if (!this.qtyInput) return;
        let val = parseInt(this.qtyInput.value, 10);
        const min = parseInt(this.qtyInput.min, 10) || 1;
        const max = this.qtyInput.max
          ? parseInt(this.qtyInput.max, 10)
          : Infinity;
        const step = parseInt(this.qtyInput.step, 10) || 1;

        if (Number.isNaN(val) || val < min) val = min;
        if (val > max) val = max;
        // Snap to step
        val = min + Math.round((val - min) / step) * step;
        this.qtyInput.value = val;

        this._highlightVolumeTier();
      }

      _updateQtyRules(variant) {
        if (!this.qtyInput || !this.priceBreaks) return;
        const rules = this.priceBreaks[String(variant.id)];
        if (!rules) return;

        this.qtyInput.min = rules.min;
        this.qtyInput.step = rules.increment;
        this.qtyInput.value = rules.min;
        if (rules.max !== null) {
          this.qtyInput.max = rules.max;
        } else {
          this.qtyInput.removeAttribute('max');
        }
      }

      // ---- Volume Pricing (Mengenkauf) ------------------------------------

      _updateVolumePricing(variant) {
        if (!this.volumePricingEl || !this.priceBreaks) return;
        const data = this.priceBreaks[String(variant.id)];
        if (!data || !data.breaks || data.breaks.length === 0) {
          this.volumePricingEl.innerHTML = '';
          return;
        }

        let html = '<span class="quick-atc-drawer__volume-title">' +
          'Mengenrabatt</span>';
        html += '<ul class="quick-atc-drawer__volume-list">';
        html +=
          '<li class="quick-atc-drawer__volume-tier">' +
          `<span>${data.min}+</span>` +
          `<span>${this._formatMoney(data.price)}/Stück</span>` +
          '</li>';
        data.breaks.forEach((brk) => {
          html +=
            '<li class="quick-atc-drawer__volume-tier">' +
            `<span>${brk.minimum_quantity}+</span>` +
            `<span>${this._formatMoney(brk.price)}/Stück</span>` +
            '</li>';
        });
        html += '</ul>';
        this.volumePricingEl.innerHTML = html;

        // Reset qty to min and highlight active tier
        if (this.qtyInput) this.qtyInput.value = data.min;
        this._highlightVolumeTier();
      }

      _highlightVolumeTier() {
        if (!this.volumePricingEl) return;
        const qty = this.qtyInput
          ? parseInt(this.qtyInput.value, 10)
          : 1;
        const tiers = [
          ...this.volumePricingEl.querySelectorAll(
            '.quick-atc-drawer__volume-tier'
          ),
        ];
        let activeIdx = 0;
        tiers.forEach((tier, i) => {
          tier.classList.remove('quick-atc-drawer__volume-tier--active');
          const minQty = parseInt(
            tier.querySelector('span').textContent, 10
          );
          if (qty >= minQty) activeIdx = i;
        });
        if (tiers[activeIdx]) {
          tiers[activeIdx].classList.add(
            'quick-atc-drawer__volume-tier--active'
          );
        }
      }

      _trapFocus() {
        if (!this.panel) return;
        const focusable = this.panel.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length) focusable[0].focus({ preventScroll: true });
      }
    }
  );
}
